import { useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { LoadingState } from '@/components/ui';
import AssessmentResultBanner from '@/components/assessment/AssessmentResultBanner';
import Doch33ReportView from '@/components/planning/Doch33ReportView';
import type { AppLanguage } from '@/i18n/translations';
import { ForcedLocaleShell } from '@/i18n/LocaleProvider';
import { isRtlLocale, parseAppLanguage } from '@/lib/planning-report-i18n';
import { useTranslation } from '@/i18n/useTranslation';
import { useSessionAssessment } from '@/hooks/useSessionAssessment';
import { usePlanningInfoReport } from './usePlanningInfoReport';
import styles from './PlanningInfoPrintScreen.module.css';

function PlanningInfoPrintContent({
  sessionId,
  languageOverride,
}: {
  sessionId: string | undefined;
  languageOverride?: AppLanguage;
}) {
  const t = useTranslation();
  const {
    planningInfo,
    view,
    loading,
    error,
    documentRef,
    parcelLine,
    labels,
    language,
  } = usePlanningInfoReport(sessionId, languageOverride);
  const { assessment } = useSessionAssessment(sessionId);

  const assessmentLabels = {
    status: t.assessment.status,
    trafficLight: t.assessment.trafficLight,
    unknownNote: t.assessment.unknownNote,
  };

  const ready = Boolean(view && planningInfo && !loading && !error);

  useEffect(() => {
    if (ready) {
      document.documentElement.setAttribute('data-print-ready', 'true');
    } else {
      document.documentElement.removeAttribute('data-print-ready');
    }
    return () => document.documentElement.removeAttribute('data-print-ready');
  }, [ready]);

  useEffect(() => {
    if (!ready) return;
    const autoprint = new URLSearchParams(window.location.search).get('autoprint') === '1';
    if (!autoprint) return;
    const timer = window.setTimeout(() => window.print(), 400);
    return () => window.clearTimeout(timer);
  }, [ready]);

  if (loading) {
    return <LoadingState variant="fullPage" label={labels.title} branded />;
  }

  if (error || !planningInfo || !view) {
    return (
      <p className={styles.error} data-testid="planning-print-error">
        {error ?? 'Failed to load planning report'}
      </p>
    );
  }

  return (
    <div className={styles.page} data-testid="planning-print-page">
      {assessment ? (
        <AssessmentResultBanner
          className={styles.ramzorBanner}
          assessment={assessment}
          labels={assessmentLabels}
          sectionLabel={t.planningInfo.ramzorLabel}
          variant="compact"
          showSummary={false}
          data-testid="planning-print-ramzor"
        />
      ) : null}
      <Doch33ReportView
        variant="print"
        view={view}
        dir={isRtlLocale(language) ? 'rtl' : 'ltr'}
        coverBadge={labels.coverBadge}
        reportHeading={labels.title}
        reportSubtitle={labels.reportCoverSubtitle}
        reportMeta={view.reportMeta}
        reportDateLabel={labels.reportDate}
        requestTypeLabel={labels.requestType}
        parcelLine={parcelLine}
        documentRefLabel={labels.reportDocumentRef}
        documentRef={documentRef}
        informationalNotice={labels.informationalNotice}
        producedByLine={labels.pdfProducedBy}
        verifySourceLabel={labels.verifySourceLabel}
        openPdfOnPageLabel={labels.openPdfOnPageLabel}
        extractionDraftLabel={labels.extractionDraftLabel}
      />
    </div>
  );
}

/**
 * Minimal report-only page for Playwright PDF export (vector HTML, full app CSS).
 * Backend opens /planning-info/print/:sessionId?lang=… and calls page.pdf().
 */
export default function PlanningInfoPrintScreen() {
  const { sessionId } = useParams<{ sessionId: string }>();
  const [searchParams] = useSearchParams();
  const langOverride = parseAppLanguage(searchParams.get('lang'));

  if (langOverride) {
    return (
      <ForcedLocaleShell language={langOverride}>
        <PlanningInfoPrintContent sessionId={sessionId} languageOverride={langOverride} />
      </ForcedLocaleShell>
    );
  }

  return <PlanningInfoPrintContent sessionId={sessionId} />;
}
